import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { loadShowFinale, saveShowFinale, uploadAttachment, isUploadConfigured } from '../integrations/quizApiAdapter';
import resolveAssetUrl from '../utils/assets';
import Button from './ui/Button';
import FinaleFinalfrageRegie from './FinaleFinalfrageRegie';
import './css/Finalfrage.css';

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

const DEFAULT_CONFIG = { countdownSeconds: 20 };

function makeEmptyQuestion() {
    return { category: '', text: '', answers: ['', ''], correctIndex: 0, attachments: [] };
}

function normalizeLoadedQuestion(raw) {
    if (!raw || typeof raw !== 'object') return null;
    const answers = Array.isArray(raw.answers) && raw.answers.length ? raw.answers.map((a) => String(a ?? '')) : ['', ''];
    let correctIndex = Number.isInteger(raw.correctIndex) ? raw.correctIndex : 0;
    if (correctIndex < 0 || correctIndex >= answers.length) correctIndex = 0;
    return {
        category: raw.category ?? '',
        text: raw.text ?? '',
        answers,
        correctIndex,
        attachments: Array.isArray(raw.attachments) ? raw.attachments : [],
    };
}

function QuestionEditor({ label, value, onChange, uploadConfigured, onUpload, uploading }) {
    const q = value;
    const setField = (patch) => onChange({ ...q, ...patch });
    const setAnswer = (idx, text) => {
        const answers = [...q.answers];
        answers[idx] = text;
        onChange({ ...q, answers });
    };
    const addAnswer = () => onChange({ ...q, answers: [...q.answers, ''] });
    const removeAnswer = (idx) => {
        if (q.answers.length <= 2) return;
        const answers = q.answers.filter((_, i) => i !== idx);
        let correctIndex = q.correctIndex;
        if (idx === correctIndex) correctIndex = 0;
        else if (idx < correctIndex) correctIndex -= 1;
        onChange({ ...q, answers, correctIndex });
    };
    const moveAnswer = (idx, dir) => {
        const target = idx + dir;
        if (target < 0 || target >= q.answers.length) return;
        const answers = [...q.answers];
        const [item] = answers.splice(idx, 1);
        answers.splice(target, 0, item);
        let correctIndex = q.correctIndex;
        if (correctIndex === idx) correctIndex = target;
        else if (correctIndex === target) correctIndex = idx;
        onChange({ ...q, answers, correctIndex });
    };

    const image = q.attachments?.[0] || null;

    return (
        <div className="card section-stack ff-edit">
            <div className="card-header"><h3 className="card-title">{label}</h3></div>
            <div className="input-group">
                <label>Kategorie</label>
                <input type="text" value={q.category} onChange={(e) => setField({ category: e.target.value })} placeholder="z. B. Geographie" />
            </div>
            <div className="input-group">
                <label>Frage</label>
                <input type="text" value={q.text} onChange={(e) => setField({ text: e.target.value })} placeholder="z. B. Welcher dieser Flüsse ist der längste?" />
            </div>
            <div className="input-group">
                <label>Antworten — Lösung markieren</label>
                {q.answers.map((text, idx) => (
                    <div className="ff-answer-edit" key={idx}>
                        <span className="text-muted" style={{ minWidth: 18 }}>{LETTERS[idx] || idx + 1}</span>
                        <input type="text" value={text} onChange={(e) => setAnswer(idx, e.target.value)} placeholder={`Antwort ${LETTERS[idx] || idx + 1}`} />
                        <label className={`ff-correct-toggle ${q.correctIndex === idx ? 'is-correct' : ''}`}>
                            <input
                                type="radio"
                                name={`${label}-correct`}
                                checked={q.correctIndex === idx}
                                onChange={() => setField({ correctIndex: idx })}
                                style={{ width: 'auto' }}
                            />
                            Lösung
                        </label>
                        <Button size="sm" variant="ghost" onClick={() => moveAnswer(idx, -1)} disabled={idx === 0}>↑</Button>
                        <Button size="sm" variant="ghost" onClick={() => moveAnswer(idx, 1)} disabled={idx === q.answers.length - 1}>↓</Button>
                        <Button size="sm" variant="danger" onClick={() => removeAnswer(idx)} disabled={q.answers.length <= 2}>✕</Button>
                    </div>
                ))}
                <div className="button-row">
                    <Button size="sm" onClick={addAnswer}>+ Antwort hinzufügen</Button>
                </div>
                <span className="text-muted" style={{ fontSize: '0.85em' }}>Mindestens 2, nach oben offen. Der Moderator blendet sie einzeln ein.</span>
            </div>
            <div className="input-group">
                <label>Bild (optional)</label>
                {image && (
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <img src={resolveAssetUrl(image.url)} alt="" style={{ maxHeight: 80, borderRadius: 8 }} />
                        <Button size="sm" variant="ghost" onClick={() => setField({ attachments: [] })}>Entfernen</Button>
                    </div>
                )}
                {uploadConfigured ? (
                    <input type="file" accept="image/*" disabled={uploading} onChange={(e) => onUpload(e.target.files?.[0])} />
                ) : (
                    <span className="text-muted" style={{ fontSize: '0.85em' }}>Upload nicht konfiguriert.</span>
                )}
                {uploading && <span className="text-muted">Lädt hoch…</span>}
            </div>
        </div>
    );
}

function FinaleFinalfrageView({ playsetId }) {
    const [config, setConfig] = useState(DEFAULT_CONFIG);
    const [question, setQuestion] = useState(makeEmptyQuestion());
    const [reserveQuestion, setReserveQuestion] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [saveStatus, setSaveStatus] = useState('');
    const [busy, setBusy] = useState(false);
    const [uploadingTarget, setUploadingTarget] = useState(null);
    const [settingsOpen, setSettingsOpen] = useState(false);

    const load = useCallback(async () => {
        if (!playsetId) {
            setLoading(false);
            setError('Keine aktive Show. Bitte zuerst eine Show aktivieren.');
            return;
        }
        setLoading(true);
        setError('');
        try {
            const finale = await loadShowFinale(playsetId);
            const state = finale?.state || {};
            setConfig({ ...DEFAULT_CONFIG, ...(state.config || {}) });
            setQuestion(normalizeLoadedQuestion(state.question) || makeEmptyQuestion());
            setReserveQuestion(normalizeLoadedQuestion(state.reserveQuestion));
        } catch (err) {
            setError(err?.message || 'Fehler beim Laden des Finalfrage-Finales.');
        } finally {
            setLoading(false);
        }
    }, [playsetId]);

    useEffect(() => { load(); }, [load]);

    const persist = useCallback(async () => {
        setBusy(true);
        setSaveStatus('');
        try {
            await saveShowFinale(playsetId, {
                kind: 'finalfrage',
                state: { config, question, reserveQuestion },
            });
            setSaveStatus('Gespeichert.');
            setTimeout(() => setSaveStatus(''), 2500);
        } catch (err) {
            setError(err?.message || 'Fehler beim Speichern.');
        } finally {
            setBusy(false);
        }
    }, [playsetId, config, question, reserveQuestion]);

    const handleUpload = useCallback(async (target, file) => {
        if (!file) return;
        setUploadingTarget(target);
        setError('');
        try {
            const result = await uploadAttachment(file);
            const attachment = { url: result.url, mime: result.mime, originalName: result.originalName, size: result.size, placement: 'above' };
            if (target === 'main') setQuestion((q) => ({ ...q, attachments: [attachment] }));
            else setReserveQuestion((q) => ({ ...(q || makeEmptyQuestion()), attachments: [attachment] }));
        } catch (err) {
            setError(err?.message || 'Bild-Upload fehlgeschlagen.');
        } finally {
            setUploadingTarget(null);
        }
    }, []);

    const uploadConfigured = useMemo(() => isUploadConfigured(), []);

    if (loading) return <div className="page text-muted">Finalfrage-Finale wird geladen…</div>;

    return (
        <div className="page section-stack">
            <FinaleFinalfrageRegie />

            {error && (
                <div className="error-banner" style={{ background: '#fdecea', color: '#b71c1c', padding: '8px', borderRadius: '4px' }}>
                    {error}
                </div>
            )}

            <button type="button" className="ff-settings-fab" onClick={() => setSettingsOpen(true)}>Einstellungen</button>
            {settingsOpen && (
                <div className="ff-settings-overlay" onClick={() => setSettingsOpen(false)}>
                    <aside className="ff-settings-drawer" onClick={(e) => e.stopPropagation()}>
                        <div className="card-header">
                            <h3 className="card-title">Einstellungen</h3>
                            <Button variant="ghost" size="sm" onClick={() => setSettingsOpen(false)}>Schließen</Button>
                        </div>
                        <div className="input-group">
                            <label>Countdown-Dauer (Sekunden)</label>
                            <input
                                type="number"
                                min="3"
                                max="600"
                                value={config.countdownSeconds}
                                onChange={(e) => setConfig((c) => ({ ...c, countdownSeconds: Number(e.target.value) || 20 }))}
                            />
                        </div>
                        <div className="button-row">
                            <Button variant="primary" onClick={() => { persist(); setSettingsOpen(false); }}>Speichern &amp; schließen</Button>
                        </div>
                    </aside>
                </div>
            )}

            <QuestionEditor
                label="Finalfrage"
                value={question}
                onChange={setQuestion}
                uploadConfigured={uploadConfigured}
                uploading={uploadingTarget === 'main'}
                onUpload={(file) => handleUpload('main', file)}
            />

            <div className="card section-stack">
                <div className="card-header">
                    <h3 className="card-title">Reserve-Frage</h3>
                    {reserveQuestion ? (
                        <Button size="sm" variant="danger" onClick={() => setReserveQuestion(null)}>Entfernen</Button>
                    ) : (
                        <Button size="sm" onClick={() => setReserveQuestion(makeEmptyQuestion())}>Reserve anlegen</Button>
                    )}
                </div>
                {reserveQuestion ? (
                    <QuestionEditor
                        label="Reserve"
                        value={reserveQuestion}
                        onChange={setReserveQuestion}
                        uploadConfigured={uploadConfigured}
                        uploading={uploadingTarget === 'reserve'}
                        onUpload={(file) => handleUpload('reserve', file)}
                    />
                ) : (
                    <div className="text-muted">Optional. Der Moderator kann sie in Phase „abgerechnet“ laden (z. B. bei Gleichstand).</div>
                )}
            </div>

            <div className="button-row">
                <Button variant="primary" onClick={persist} disabled={busy || !playsetId}>
                    {busy ? 'Speichere…' : 'Speichern'}
                </Button>
                {saveStatus && <span className="text-muted">{saveStatus}</span>}
            </div>
        </div>
    );
}

export default FinaleFinalfrageView;
